import React, {useCallback, useEffect, useState} from 'react';
import {InputGroup, Button, TextArea, Card, H3, Popover, MenuItem, Tooltip} from '@blueprintjs/core'
import {Suggest} from '@blueprintjs/select'
import styled from 'styled-components'

import {Table, Row, Cell} from '/src/components/tables/table'
import {device} from "/src/services/iot-api";
import {recordings} from "/src/services/api";
import {errorToast} from "/src/utils/toaster";


const CreatingCardContainer = styled.div`
    display: block;
    padding: 1em;
    
`

const SourceText = styled.span`
    word-break: break-all;
`

// The camera registry entries carry the rtsp url plus whatever else the device knows about the
// camera; keep the url as the value and use any human readable field as a hint.
const asCamera = (entry) => {
    if (typeof entry === 'string')
        return entry.startsWith('rtsp://') ? {url: entry, label: null} : null;
    if (!entry || typeof entry !== 'object')
        return null;
    const url = entry.rtsp_url;
    if (typeof url !== 'string' || !url.startsWith('rtsp://'))
        return null;
    const label = [entry.name, entry.manufacturer, entry.model, entry.ip, entry.hostname]
        .find(v => typeof v === 'string' && v.length > 0);
    return {url, label: label ?? null};
}

// GetThumbnailCommand answers with a base64 jpeg
const base64JpegToBlob = (base64) => new Blob(
    [new Uint8Array(atob(base64).split('').map(c => c.charCodeAt(0)))],
    {type: 'image/jpg'}
);

// `width` defaults to whatever keeps the aspect ratio, pass it to resize to an exact size
const resizeBlob = (blob, height = 480, width = null, quality = 0.7) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(blob);

        img.onload = () => {

            const ratio = height / img.height;
            // Draw onto canvas
            const canvas = document.createElement("canvas");
            canvas.width = width ?? ratio * img.width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Export as Base64 (jpeg with 70% quality by default)
            const base64String = canvas.toDataURL("image/jpeg", quality);
            URL.revokeObjectURL(url); // Clean up
            resolve({img: base64String.split(",")[1], height: canvas.height, width: canvas.width});
        };

        img.onerror = reject;
        img.src = url;
    });
}

const CreatingCard = ({onConfirm}) => {
    const [nameInput, setNameInput] = useState("");

    return <CreatingCardContainer>
        <H3>Create thumbnail</H3>
        <InputGroup
            type="text"
            leftIcon="edit"
            placeholder="Camera name"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter'? onConfirm(nameInput) : null}
        />
        <Button
            fill={true}
            text="Create recid"
            intent="primary"
            disabled={nameInput.length === 0}
            onClick={() => onConfirm(nameInput)}
        />

    </CreatingCardContainer>
}


const Thumbnail = ({thumbnail, onCreate, onRemove}) => {
    const [loading, setLoading] = useState(false);
    const [showButtons, setShowButtons] = useState(false);

    return <Table
        onMouseOver={() => setShowButtons(true)}
        onMouseLeave={() => setShowButtons(false)}
    >
        <Row margin='auto auto 20px auto' $separatorColor='silver'></Row>

        <Row>
            <Cell halign="center" valign="center">
                <img src={URL.createObjectURL(thumbnail.blob)} alt="" width="100%"/>
            </Cell>
        </Row>
        <Row>
            <Cell span={5}>
                <TextArea
                    fill={true}
                    disabled={true}
                    value={thumbnail.url}
                    autoResize={true}
                />
            </Cell>
            {showButtons ? <Cell span={1}>
                <div>
                    <Button
                        minimal={true}
                        icon="add"
                        title="Create recording"
                        loading={loading}
                        onClick={() => {
                            if (onCreate) {
                                setLoading(true);
                                onCreate(thumbnail.url)
                                setLoading(false);
                            }
                        }}
                    />
                    <Button
                        minimal={true}
                        icon="trash"
                        title="Remove"
                        loading={loading}
                        onClick={() => onRemove && onRemove(thumbnail)}
                    />
                </div>
            </Cell> : null}
        </Row>
    </Table>
}


const ThumbnailsControl = ({deviceName, onCreate}) => {
    const [thumbnails, setThumbnails] = useState([]);
    const [currentText, setCurrentText] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [loadingCameras, setLoadingCameras] = useState(false);

    useEffect(() => {
        console.log(creating);
    }, [creating]);

    const fetchCameras = useCallback((notify = false) => {
        setLoadingCameras(true);
        console.log(`Getting registered cameras on ${deviceName}`);
        device.command('GetCameraRegistryCommand', deviceName).then((resp) => {
            const registry = resp.payload?.resp;
            if (registry?.error) {
                if (notify) errorToast({message: registry.error});
            } else if (Array.isArray(registry)) {
                const found = registry.map(asCamera).filter(Boolean);
                // the registry can hold several entries per url (different profiles/probes)
                setCameras([...new Map(found.map(c => [c.url, c])).values()]);
            } else {
                console.log('Unexpected camera registry response', registry);
                if (notify) errorToast({message: 'Failed to get the camera registry'});
            }
        }).catch((err) => {
            console.log(err);
            if (notify) errorToast({message: err?.message ?? String(err)});
        }).finally(() => setLoadingCameras(false));
    }, [deviceName]);

    useEffect(() => {
        fetchCameras();
    }, [fetchCameras]);

    return <Table>
        <Row>
            <Cell span={1}>
                <Button
                    // style={{padding: '10px'}}
                    text="Request"
                    intent="primary"
                    loading={loading}
                    onClick={() => {
                        if (currentText) {
                            console.log(`Requesting ${currentText}`);
                            setLoading(true);

                            device.command(
                                'GetThumbnailCommand', deviceName, {source: currentText}
                            ).then((resp) => {
                                console.log(`Finished requesting ${currentText}`);
                                if (resp.payload?.resp?.error) {
                                    // if the response from the IoTWrapper has an error
                                    errorToast({message: resp.payload.resp.error})
                                } else {
                                    // ----- construct blob from response
                                    const blob = base64JpegToBlob(resp.payload.resp);
                                    setThumbnails(
                                        [
                                            ...thumbnails,
                                            {url: currentText, blob: blob}
                                        ].map((t, i) => ({...t, id: i}))
                                    );
                                    setCurrentText('');
                                }
                            }).catch((err) => {
                                console.log(err);
                                errorToast({message: err?.message ?? String(err)})
                            }).finally(() => setLoading(false));
                        } else {
                            console.log("Nothing to request");
                            errorToast({message: "Please specify an RTSP url"});
                        }
                    }}
                />
            </Cell>
            <Cell span={6}>
                <Suggest
                    fill
                    disabled={loading}
                    items={cameras}
                    // fully controlled by `currentText`: a null selectedItem keeps Suggest from
                    // replacing the input with the last picked camera once the popover closes
                    selectedItem={null}
                    query={currentText}
                    onQueryChange={setCurrentText}
                    onItemSelect={camera => setCurrentText(camera.url)}
                    inputValueRenderer={camera => camera.url}
                    itemPredicate={(query, camera) =>
                        `${camera.url} ${camera.label ?? ''}`.toLowerCase().includes(query.toLowerCase())
                    }
                    itemRenderer={(camera, {handleClick, modifiers}) => modifiers.matchesPredicate ? (
                        <MenuItem
                            key={camera.url}
                            active={modifiers.active}
                            text={<SourceText>{camera.url}</SourceText>}
                            label={camera.label ?? undefined}
                            onClick={handleClick}
                        />
                    ) : null}
                    noResults={
                        <MenuItem
                            disabled={true}
                            text={
                                loadingCameras ? "Loading registered cameras…"
                                    : cameras.length ? "No matching camera, the url will be used as typed."
                                        : "No registered cameras found on this device."
                            }
                        />
                    }
                    popoverProps={{minimal: true, matchTargetWidth: true}}
                    inputProps={{
                        small: true,
                        placeholder: loadingCameras
                            ? "Loading registered cameras…"
                            : "Pick a registered camera or enter an rtsp url",
                        rightElement: (
                            <Tooltip content="Reload the device camera registry" compact minimal>
                                <Button
                                    minimal
                                    small
                                    icon="refresh"
                                    disabled={loading}
                                    loading={loadingCameras}
                                    onClick={() => fetchCameras(true)}
                                />
                            </Tooltip>
                        ),
                    }}
                />
            </Cell>
        </Row>
        {thumbnails.map((thumbnail) => {
            return <Row key={thumbnail.id}>
                <Cell>
                    <Popover
                        isOpen={!!creating && creating.id === thumbnail.id}
                        onClose={() => setCreating(null)}
                        content={
                            <CreatingCard
                                onConfirm={
                                    (name) => {
                                        console.log(`Creating ${name}`);
                                        resizeBlob(creating.blob).then(({img, width, height}) => {
                                            onCreate({name, img, width, height, source: creating.url});
                                        }).finally(() => setCreating(null));
                                    }
                                }
                            />
                        }
                    >
                        <Thumbnail
                            thumbnail={thumbnail}
                            onCreate={() => setCreating(thumbnail)}
                            onRemove={(thumb) => {
                                console.log(`Remove thumbnail ${thumb.id}`);
                                thumbnails.splice(thumb.id, 1);
                                setThumbnails([...thumbnails].map((t, i) => ({...t, id: i})));
                            }}
                        />
                    </Popover>
                </Cell>
            </Row>
        })}
    </Table>
}

export {ThumbnailsControl, base64JpegToBlob, resizeBlob};